import {
  ArrowUpTrayIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid';
import { useFormikContext } from 'formik';
import { useRef, useState } from 'react';
import { Loading } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useWorkspaceContext } from '../../../../../../../../providers/WorkspaceProvider/Workspace.context';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook';
import { eAlertType } from '../../../../../../../../providers/ToastProvider/Toast.enum';
import { FileUploadService } from '../../../../../../../../services/FileUpload/FileUpload.service';
import {
  INTERACTIVE_LIMITS,
  tInteractiveHeader,
  tInteractiveHeaderType,
  tInteractiveOutbound,
} from '../../../../../../Chat.types';
import UTF16Counter from '../UTF16Counter/UTF16Counter.component';

// Meta's allowed MIME types per header variant. The `accept` attribute filters
// the OS file picker; we still validate size + extension on the upload path.
const HEADER_ACCEPT: Record<Exclude<tInteractiveHeaderType, 'text'>, string> = {
  image: 'image/jpeg,image/png',
  video: 'video/mp4,video/3gpp',
  document:
    'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain',
};

// Meta's per-type size caps (MB). The S3 layer caps at 100 MB, these caps are
// stricter so we fail fast instead of uploading then getting rejected by Meta.
const HEADER_SIZE_MB: Record<Exclude<tInteractiveHeaderType, 'text'>, number> = {
  image: 5,
  video: 16,
  document: 100,
};

const HEADER_ICONS: Record<Exclude<tInteractiveHeaderType, 'text'>, typeof PhotoIcon> = {
  image: PhotoIcon,
  video: VideoCameraIcon,
  document: DocumentIcon,
};

type tUploadState =
  | { kind: 'idle' }
  | { kind: 'uploading' }
  | { kind: 'error'; message: string };

// For `list` variant, header is text-only per Meta spec. For `button` and
// `cta_url`, media headers (image / video / document) are allowed. Media is
// uploaded via the shared FileUploadService (same S3 flow the attachment
// PaperClip uses) and stored as a `link` URL on the header — Meta accepts
// either `{link}` or `{id}` and we prefer `link` to avoid the Meta media-id
// upload dance entirely.
export default function InteractiveHeaderPicker() {
  const { t } = useTranslation(['chat/chat']);
  const { values, setFieldValue } = useFormikContext<tInteractiveOutbound>();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { addAlert } = useToastContext();

  const [uploadState, setUploadState] = useState<tUploadState>({ kind: 'idle' });
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isList = values.type === 'list';
  const headerTypes: tInteractiveHeaderType[] = isList
    ? ['text']
    : ['text', 'image', 'video', 'document'];

  const currentHeader = values.header;
  // Narrow once — `list` variant restricts to text-only at the type level,
  // so on the button/cta_url branches the header is tInteractiveHeader (the
  // wider union). Cast covers the runtime guarantee that isn't expressible
  // through formik's generic.
  const mediaHeader =
    currentHeader && currentHeader.type !== 'text'
      ? (currentHeader as tInteractiveHeader)
      : null;
  const mediaType = mediaHeader
    ? (mediaHeader.type as Exclude<tInteractiveHeaderType, 'text'>)
    : null;
  const currentLink =
    mediaHeader && mediaType ? mediaHeader[mediaType]?.link : undefined;

  async function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // reset so the same file can be re-picked after clear
    if (!file || !mediaType) return;

    if (!currentWorkspaceId) {
      setUploadState({
        kind: 'error',
        message: t('interactive.header.uploadErrorNoWorkspace'),
      });
      return;
    }

    const maxMb = HEADER_SIZE_MB[mediaType];
    const fileSizeMb = file.size / 1024 / 1024;
    if (fileSizeMb > maxMb) {
      const msg = t('interactive.header.uploadErrorTooLarge', {
        actual: fileSizeMb.toFixed(1),
        max: maxMb,
      });
      setUploadState({ kind: 'error', message: msg });
      addAlert({ type: eAlertType.ERROR, message: msg });
      return;
    }

    const allowedMimes = HEADER_ACCEPT[mediaType].split(',');
    if (!allowedMimes.includes(file.type)) {
      const msg = t('interactive.header.uploadErrorInvalidType', {
        type: file.type || 'unknown',
      });
      setUploadState({ kind: 'error', message: msg });
      addAlert({ type: eAlertType.ERROR, message: msg });
      return;
    }

    setUploadState({ kind: 'uploading' });
    try {
      const url = await FileUploadService.uploadFileToS3({
        file,
        workspaceId: currentWorkspaceId,
      });
      // Document headers carry `filename` on the wire — Meta uses it as the
      // display title for the recipient's document preview. Image and video
      // headers don't (Meta ignores the field). We capture the original
      // user-facing name so it survives the round-trip instead of falling
      // back to the hashed S3 path segment.
      const ref =
        mediaType === 'document'
          ? { link: url, filename: file.name }
          : { link: url };
      setFieldValue(`header.${mediaType}`, ref);
      setFileName(file.name);
      setUploadState({ kind: 'idle' });
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('interactive.header.uploadErrorGeneric');
      setUploadState({ kind: 'error', message: msg });
      addAlert({ type: eAlertType.ERROR, message: msg });
    }
  }

  function handleClearMedia() {
    if (!mediaType) return;
    setFieldValue(`header.${mediaType}`, {});
    setFileName(null);
    setUploadState({ kind: 'idle' });
  }

  function handleTypeChange(type: tInteractiveHeaderType | null) {
    if (type === null) {
      setFieldValue('header', undefined);
      setFileName(null);
      setUploadState({ kind: 'idle' });
      return;
    }
    if (type === 'text') {
      setFieldValue('header', { type: 'text', text: '' });
      setFileName(null);
      setUploadState({ kind: 'idle' });
      return;
    }
    setFieldValue('header', { type, [type]: {} });
    setFileName(null);
    setUploadState({ kind: 'idle' });
  }

  return (
    <div className="InteractiveAction__Section">
      <div className="InteractiveAction__Field">
        <label className="InteractiveAction__Field__Label">
          {t('interactive.header.label')}
        </label>
        <div className="flex gap-2 flex-wrap">
          <label className="flex items-center gap-1 text-sm">
            <input
              type="radio"
              name="header.enabled"
              checked={!currentHeader}
              className="radio radio-xs"
              onChange={() => handleTypeChange(null)}
            />
            <span>{t('interactive.header.none')}</span>
          </label>
          {headerTypes.map((type) => (
            <label key={type} className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                name="header.type"
                value={type}
                checked={currentHeader?.type === type}
                className="radio radio-xs"
                onChange={() => handleTypeChange(type)}
              />
              <span>{t(`interactive.header.types.${type}`)}</span>
            </label>
          ))}
        </div>

        {currentHeader?.type === 'text' && (
          <div className="InteractiveAction__Field">
            <input
              type="text"
              dir="auto"
              maxLength={INTERACTIVE_LIMITS.HEADER_TEXT}
              className="InteractiveAction__Field__Input"
              placeholder={t('interactive.header.textPlaceholder')}
              value={currentHeader.text ?? ''}
              onChange={(e) => setFieldValue('header.text', e.target.value)}
            />
            <UTF16Counter
              value={currentHeader.text}
              max={INTERACTIVE_LIMITS.HEADER_TEXT}
            />
          </div>
        )}

        {mediaType && (
          <div className="InteractiveAction__Field">
            <label className="InteractiveAction__Field__Label">
              {t('interactive.header.mediaUploadLabel', {
                type: t(`interactive.header.types.${mediaType}`),
              })}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept={HEADER_ACCEPT[mediaType]}
              className="hidden"
              onChange={handleFileSelect}
            />

            {!currentLink && uploadState.kind !== 'uploading' && (
              <button
                type="button"
                className="InteractiveAction__MediaUpload__Button"
                onClick={() => fileInputRef.current?.click()}
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                <span>{t('interactive.header.uploadCta')}</span>
              </button>
            )}

            {uploadState.kind === 'uploading' && (
              <div className="InteractiveAction__MediaUpload__Status">
                <Loading size="sm" />
                <span>{t('interactive.header.uploading')}</span>
              </div>
            )}

            {currentLink && uploadState.kind !== 'uploading' && (
              <div className="InteractiveAction__MediaUpload__Uploaded">
                {(() => {
                  const Icon = HEADER_ICONS[mediaType];
                  return <Icon className="w-4 h-4 text-emerald-600" />;
                })()}
                <span
                  className="InteractiveAction__MediaUpload__FileName"
                  title={fileName ?? currentLink}
                >
                  {fileName ?? currentLink}
                </span>
                <button
                  type="button"
                  className="InteractiveAction__MediaUpload__Clear"
                  onClick={handleClearMedia}
                  aria-label={t('interactive.header.clearMedia')}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            )}

            {uploadState.kind === 'error' && (
              <p className="InteractiveAction__Field__Error">
                {uploadState.message}
              </p>
            )}

            <div className="InteractiveAction__MediaUpload__Footer">
              <p className="InteractiveAction__MediaUpload__Help">
                {t('interactive.header.uploadHelp', {
                  max: HEADER_SIZE_MB[mediaType],
                })}
              </p>
              <span
                className={`InteractiveAction__Field__Counter${
                  currentLink
                    ? ' InteractiveAction__Field__Counter--complete'
                    : ''
                }`}
                aria-label={t('interactive.header.mediaCountAria')}
              >
                {currentLink ? 1 : 0} / 1
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
